import { Stack } from '@mui/joy';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useNotificationContext, useStepsContext } from 'core/states';
import { IStep, IStepButtonOptions } from 'core/types';
import { keepFocusOnMouseDown, markSubmitIntent } from 'core/utils';
import { Color, Direction, Variant } from 'theme/enums';

const StepButtons = ({ step }: { step: IStep }) => {
  const { t } = useTranslation();

  const {
    activeStepIndex,
    goToPreviousStep,
    goToNextStep,
    handleOnClose,
    isFinalStep,
    isLoadingNextStep,
  } = useStepsContext();

  const { getIsLoadingNotificationDisplayed } = useNotificationContext();

  const previousButton = step?.previousButton;
  const nextButtons =
    step?.nextButtons?.length > 0 ? step.nextButtons : step?.nextButton ? [step.nextButton] : [];
  const cancelButton = step?.cancelButton;

  const disabledButtons =
    getIsLoadingNotificationDisplayed(NotificationPosition.DRAWER) || isLoadingNextStep;

  const onNextMouseDown = (event: MouseEvent<HTMLElement>, options?: IStepButtonOptions) => {
    keepFocusOnMouseDown(event);

    if (isFinalStep) {
      markSubmitIntent();
    }

    goToNextStep(options);

    // The button no longer takes the focus, so the edited field is blurred here, after the step
    // has started as before: a mid-wizard Next keeps the on-blur pricing preview of that field.
    if (!isFinalStep && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <>
      <Stack direction={Direction.ROW} spacing={0.5} alignItems={'center'}>
        {activeStepIndex > 0 && (
          <Button
            text={t('buttonLabelBack')}
            color={Color.NEUTRAL}
            variant={Variant.OUTLINED}
            startDecorator={<CaretLeft />}
            {...previousButton}
            onClick={() => goToPreviousStep(previousButton?.options)}
            sx={{ height: 'fit-content' }}
            disabled={previousButton?.options?.disabled || disabledButtons}
          />
        )}

        <Stack direction={Direction.ROW} spacing={0.5}>
          {nextButtons.map((button, index) => {
            return (
              <Button
                key={index}
                text={t('buttonLabelNext')}
                endDecorator={!isFinalStep ? <CaretRight /> : null}
                // Only the final step's button is a real Save; tag it so on-blur pricing previews
                // are skipped when focus moves to it (mid-wizard "Next" keeps its preview).
                // `|| undefined` is load-bearing — isSubmitButtonBlur matches on attribute
                // PRESENCE, so `={false}` would still render the attribute (as "false") and
                // wrongly suppress previews on mid-wizard Next. Don't "fix" it to `={isFinalStep}`.
                data-suppress-blur-preview={isFinalStep || undefined}
                {...button}
                onMouseDown={(event: MouseEvent<HTMLElement>) =>
                  onNextMouseDown(event, button?.options)
                }
                disabled={button?.options?.disabled || disabledButtons}
              />
            );
          })}

          {nextButtons.length === 0 && (
            <Button
              text={t('buttonLabelNext')}
              endDecorator={!isFinalStep ? <CaretRight /> : null}
              // `|| undefined` is load-bearing here too — see the tagged button above.
              data-suppress-blur-preview={isFinalStep || undefined}
              {...step?.nextButton}
              onMouseDown={(event: MouseEvent<HTMLElement>) =>
                onNextMouseDown(event, step.nextButton?.options)
              }
              disabled={step?.nextButton?.options?.disabled || disabledButtons}
            />
          )}
        </Stack>
      </Stack>

      <Button
        text={t('buttonLabelCancel')}
        color={Color.NEUTRAL}
        variant={Variant.OUTLINED}
        startDecorator={<X />}
        {...cancelButton}
        onClick={() => handleOnClose(cancelButton?.options)}
        disabled={cancelButton?.options?.disabled || disabledButtons}
      />
    </>
  );
};

export default StepButtons;
